import React, { useEffect, useMemo } from 'react';
import {
    Box,
    Button,
    IconButton,
    Paper,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import AddIcon from '@mui/icons-material/Add';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DIdAutocomplete, DMultipleAutocomplete } from '../../../../dum';
import { formatFloat } from '../../../../../helpers';
import { RateAggregate, resolveRateState } from './prediction';
import { NotesBulletList } from './notes-bullet-list';
import { PlanRowProduct, PlanTooltip } from './plan-row-product';
import {
    DEFAULT_EFFICIENCY,
    effectiveKgHr,
    effectiveShiftHours,
    HOURS_INCREMENT,
    HOURS_EPSILON,
    nextRowProductKey,
} from './plan-helpers';
import {
    EmployeeOption,
    InventoryEntry,
    MachineOption,
    PlanRowProductState,
    PlanRowState,
    ProductOption,
    PlannedProductContribution,
} from '../production-planning';

interface IProductionPlanRow {
    row: PlanRowState;
    index: number;
    total: number;
    // Options offered for picking a machine (filtered by branch + type).
    machines: MachineOption[];
    // Full machine list — used only to resolve the label of an already-saved
    // machine that falls outside the current filter (see machineOptions).
    allMachines: MachineOption[];
    products: ProductOption[];
    employees: EmployeeOption[];
    inventory: Map<number, InventoryEntry>;
    canEdit: boolean;
    // Hours the PLAN's turno lasts. The row's products must add up to it unless
    // the row overrides it with its own length (row.shift_hours).
    shiftHours: number;
    // employee id → how many rows (across the whole plan) already reference it.
    // Feeds the employee picker so already-assigned people show highlighted.
    employeeUsage: Map<string | number, number>;
    getRate: (
        machineId: number,
        productId: number | null,
    ) => RateAggregate | undefined;
    ensureMachineProducts: (machineId: number) => void;
    getMachineProducts: (machineId: number) => number[] | undefined;
    // Real production captured for this plan's slot, keyed `machineId:productId`.
    actualByMachineProduct: Map<string, { kilos: number; groups: number }>;
    // Forecast still to be produced, per product, across the WHOLE plan. Passed
    // down rather than derived per line so a product planned on two machines
    // projects one ending inventory instead of two that each re-add the stock.
    plannedKilosByProduct: Map<number, number>;
    // The same forecast split by machine row, for the final-inventory tooltip.
    plannedProductContributions: Map<
        number,
        PlannedProductContribution[]
    >;
    // product id → every machine in the plan running it. A line whose product
    // also runs elsewhere marks its projection, because that projection counts
    // the other machines' output too.
    plannedMachinesByProduct: Map<number, string[]>;
    // Only the expanded row mounts its inputs; the rest render a text summary.
    expanded: boolean;
    onToggleExpand: (key: string) => void;
    onChange: (index: number, patch: Partial<PlanRowState>) => void;
    onRemove: (index: number) => void;
    onMove: (index: number, direction: 'up' | 'down') => void;
}

export function ProductionPlanRow({
    row,
    index,
    total,
    machines,
    allMachines,
    products,
    employees,
    inventory,
    canEdit,
    shiftHours,
    employeeUsage,
    getRate,
    ensureMachineProducts,
    getMachineProducts,
    actualByMachineProduct,
    plannedKilosByProduct,
    plannedProductContributions,
    plannedMachinesByProduct,
    expanded,
    onToggleExpand,
    onChange,
    onRemove,
    onMove,
}: IProductionPlanRow) {
    // A saved row's machine may sit outside the current branch/type filter, so
    // it wouldn't be in `machines` and DIdAutocomplete couldn't render its
    // label. Keep the pick list filtered, but append the selected machine (from
    // the full list) when it's missing so the name always shows.
    const machineOptions = useMemo(() => {
        if (!row.machine_id) return machines;
        if (machines.some((machine) => machine.id === row.machine_id)) {
            return machines;
        }
        const selected = allMachines.find(
            (machine) => machine.id === row.machine_id,
        );
        return selected ? [...machines, selected] : machines;
    }, [machines, allMachines, row.machine_id]);

    const selectedMachine = useMemo(
        () =>
            allMachines.find((machine) => machine.id === row.machine_id) ||
            null,
        [allMachines, row.machine_id],
    );

    // Employees and products are type-restricted (they may only be assigned to
    // productions whose order_production_type matches their own), so filter both
    // lists by the picked machine's type — same rule as order-productions.
    const typeId = selectedMachine?.order_production_type_id ?? null;

    // The machine's own production history, fetched only while the row is open
    // (that is the only place a product picker exists).
    useEffect(() => {
        if (expanded && row.machine_id) {
            ensureMachineProducts(row.machine_id);
        }
    }, [expanded, row.machine_id, ensureMachineProducts]);

    const producedHere = row.machine_id
        ? getMachineProducts(row.machine_id)
        : undefined;

    // Type-compatible products, with the ones this machine has actually run
    // hoisted to the top (most-produced first). Everything else stays
    // selectable below — a product the machine has never run is exactly what a
    // NEW product is, so filtering it out would make it unpickable.
    const productOptions = useMemo(() => {
        const compatible = products.filter(
            (product) =>
                typeId === null || product.order_production_type_id === typeId,
        );
        if (!producedHere || producedHere.length === 0) return compatible;

        const rank = new Map<number, number>();
        producedHere.forEach((productId, position) =>
            rank.set(productId, position),
        );
        return [...compatible].sort((a, b) => {
            const rankA = rank.has(a.id) ? (rank.get(a.id) as number) : Infinity;
            const rankB = rank.has(b.id) ? (rank.get(b.id) as number) : Infinity;
            if (rankA !== rankB) return rankA - rankB;
            // Never-run products keep the catalogue's own order below the rest.
            return 0;
        });
    }, [products, typeId, producedHere]);

    // The same history as a membership test, so the picker can SAY which
    // products this machine has run instead of only implying it by position —
    // the order alone is invisible once the planner types and the list filters.
    const producedProductIds = useMemo(
        () => new Set(producedHere ?? []),
        [producedHere],
    );

    // Only offer employees who are NOT dado de baja (same rule as
    // order-productions' doesEmployeeHaveUpStatus), on top of the type
    // restriction. Already-saved assignments still render via the parent's full
    // employee list, so an employee later given de baja isn't hidden.
    // Operadores first, then ayudantes, each under its own header. A pareja is
    // normally one of each, so the list is sorted the way it is assembled —
    // and MUI's groupBy only emits a header when the group CHANGES between
    // adjacent options, so the sort is what makes the two sections real.
    const employeeOptions = useMemo(
        () =>
            employees
                .filter(
                    (employee) =>
                        !employee.is_inactive &&
                        (typeId === null ||
                            employee.order_production_type_id === typeId),
                )
                .sort((a, b) => {
                    const leaderA = a.is_leader ? 0 : 1;
                    const leaderB = b.is_leader ? 0 : 1;
                    if (leaderA !== leaderB) return leaderA - leaderB;
                    // Within a section, by name — the parent's list arrives in
                    // catalogue order, which is not something a planner can scan.
                    return a.fullname.localeCompare(b.fullname, undefined, {
                        sensitivity: 'base',
                    });
                }),
        [employees, typeId],
    );

    // `is_leader` is the employee's own flag (1 = operador), the same one the
    // Empleados table shows in its "Operador" column — not the per-production
    // leader of a single corrida.
    const groupEmployee = useMemo(
        () => (employee: EmployeeOption) =>
            employee.is_leader ? 'Operadores' : 'Ayudantes',
        [],
    );

    const selectedEmployees = useMemo(
        () => employees.filter((employee) => row.employee_ids.includes(employee.id)),
        [employees, row.employee_ids],
    );

    // Rate fetching is NOT here. It lives on the page, which stays mounted when
    // the WhatsApp tab is open and these rows are not — see the ratePairsKey
    // effect in production-planning.tsx. This component only reads the cache.
    const assignedHours = useMemo(
        () =>
            row.products.reduce(
                (sum, rowProduct) => sum + (rowProduct.hours || 0),
                0,
            ),
        [row.products],
    );

    // How long THIS machine's turno is: its own override when set, the plan's
    // otherwise. Every hours comparison below uses it, so the badge, the footer
    // and the save pre-check can never disagree about the target.
    const rowShiftHours = effectiveShiftHours(row.shift_hours, shiftHours);

    // A row with no products is deliberately exempt: the fresh-plan seed creates
    // one empty row per machine, and a plan of not-yet-assigned machines must
    // stay saveable. Same rule the backend enforces.
    const hoursBalanced =
        row.products.length === 0 ||
        Math.abs(assignedHours - rowShiftHours) <= HOURS_EPSILON;

    const remainingHours = Math.max(0, rowShiftHours - assignedHours);

    // What the collapsed row says. Built from the same cached rates the expanded
    // view uses, so the two never disagree.
    const summary = useMemo(() => {
        const crew = row.employee_ids
            .map(
                (id) =>
                    employees.find((employee) => employee.id === id)?.fullname,
            )
            .filter((name): name is string => !!name)
            .join(' y ');

        // There is deliberately NO row total any more. It used to close the
        // line as `≈ 403 kg · 29 bultos`, which on a one-product row simply
        // reprinted the product's own prediction in two units — and it was the
        // last place on this row still quoting a `kg · bultos` pair, which the
        // expanded view had already stopped doing. Each product carries its own
        // figures now; a two-product row is a sum a planner can do.
        const productParts = row.products.map((rowProduct) => {
            const product = products.find(
                (p) => p.id === rowProduct.product_id,
            );
            const label =
                product?.compound_description || 'Producto sin asignar';
            const rate = row.machine_id
                ? resolveRateState({
                      productId: rowProduct.product_id,
                      productRate: rowProduct.product_id
                          ? getRate(row.machine_id, rowProduct.product_id)
                          : undefined,
                      machineRate: getRate(row.machine_id, null),
                  })
                : null;

            const groupWeight = product?.current_group_weight || 0;
            // Bultos when the product has a group weight, kilos otherwise —
            // same rule and same unit the expanded line uses, so opening a row
            // never changes the units its summary was quoting.
            const showBultos = groupWeight > 0;

            // Scaled by the line's efficiency, exactly like the expanded view —
            // a collapsed row that quoted the raw historical rate would read
            // higher than the same row opened. A pair query still in flight
            // shows "…" rather than a machine average that changes a second
            // later (resolveRateState owns that rule).
            const lineKilos =
                rate?.status === 'ready' && rate.value !== null
                    ? effectiveKgHr(rate.value, rowProduct.efficiency) *
                      rowProduct.hours
                    : null;

            // `predicho → final` per product, on the closed row. `final` is the
            // PRODUCT's planning projection — full inventory plus what the WHOLE
            // plan predicts it will make — so a product also planned on another machine
            // shows a final larger than this row's own output, marked with the
            // same `*` the expanded line uses.
            const inventoryEntry = rowProduct.product_id
                ? inventory.get(rowProduct.product_id)
                : undefined;
            const plannedKilos = rowProduct.product_id
                ? plannedKilosByProduct.get(rowProduct.product_id) || 0
                : 0;
            const finalKilos = inventoryEntry
                ? inventoryEntry.kilos + plannedKilos
                : null;
            const finalInUnit =
                finalKilos === null
                    ? null
                    : showBultos && inventoryEntry
                    ? inventoryEntry.groups + plannedKilos / groupWeight
                    : finalKilos;
            // The OTHER machines making this product — this row's own filtered
            // out, so an empty array means `final` is this row's alone.
            const otherMachines = rowProduct.product_id
                ? (
                      plannedMachinesByProduct.get(rowProduct.product_id) || []
                  ).filter((name) => name !== selectedMachine?.name)
                : [];
            const sharedWithOthers = otherMachines.length > 0;

            const predictedInUnit =
                lineKilos === null
                    ? null
                    : showBultos
                    ? lineKilos / groupWeight
                    : lineKilos;

            // Production already captured for this slot, when there is any.
            // It rides along here because the same production figures are shown
            // beside the open-line inputs and in the collapsed summary.
            const actual =
                row.machine_id && rowProduct.product_id
                    ? actualByMachineProduct.get(
                          `${row.machine_id}:${rowProduct.product_id}`,
                      )
                    : undefined;
            const actualInUnit = !actual
                ? null
                : showBultos && actual.groups > 0
                ? actual.groups
                : actual.kilos;

            const unitLabel = showBultos ? 'bultos' : 'kg';
            const predictedText =
                predictedInUnit !== null
                    ? formatFloat(predictedInUnit, 0)
                    : rate?.status === 'pending'
                    ? '…'
                    : '—';
            const finalText =
                finalInUnit !== null
                    ? `${formatFloat(finalInUnit, 0)}${
                          sharedWithOthers ? '*' : ''
                      }`
                    : null;
            const actualText =
                actualInUnit !== null
                    ? `(real ${formatFloat(actualInUnit, 0)})`
                    : '';
            const inventoryInUnit = inventoryEntry
                ? showBultos
                    ? inventoryEntry.groups
                    : inventoryEntry.kilos
                : null;
            const committedInUnit = inventoryEntry
                ? showBultos
                    ? inventoryEntry.groups_committed
                    : inventoryEntry.kilos_committed
                : 0;
            const committedText =
                committedInUnit > 0
                    ? ` · ${formatFloat(committedInUnit, 0)} comprometidos`
                    : '';

            const figures = [
                predictedText,
                finalText ? ` → ${finalText}` : '',
                ` ${unitLabel}`,
                actualText ? ` ${actualText}` : '',
            ].filter((part) => part.length > 0);

            const openText =
                inventoryInUnit !== null && finalText !== null
                    ? `Inventario ${formatFloat(
                          inventoryInUnit,
                          0,
                      )} ${unitLabel}${committedText} → Predicho ${predictedText} ${unitLabel} → Final ${finalText} ${unitLabel}${
                          actualText ? ` · ${actualText}` : ''
                      }`
                    : `Predicho ${predictedText} ${unitLabel}${
                          actualText ? ` · ${actualText}` : ''
                      }`;

            // Both halves of the unit, for the tooltip. The figure on screen
            // carries one; the explanation should never be missing the other.
            const bothUnits = (kilos: number): string =>
                showBultos
                    ? `${formatFloat(kilos, 0)} kg · ${formatFloat(
                          kilos / groupWeight,
                          0,
                      )} bultos`
                    : `${formatFloat(kilos, 0)} kg`;

            // The machine average now stands in for TWO different situations —
            // no history of this product here, and history too thin to trust —
            // so the reason has to follow `productRunCount`. Keeping the old
            // "sin historial de este producto aquí" for both would state
            // something false about a product that has four runs on the machine.
            const plural = (count: number): string =>
                `${count} corrida${count === 1 ? '' : 's'}`;
            const rateSource =
                rate?.source === 'machine'
                    ? `Rendimiento tomado del promedio de ${plural(
                          rate.runCount,
                      )} de la máquina, ${
                          rate.productRunCount > 0
                              ? `porque este producto sólo tiene ${plural(
                                    rate.productRunCount,
                                )} aquí`
                              : 'sin historial de este producto aquí'
                      }`
                    : rate?.source === 'product'
                    ? `Rendimiento tomado de ${plural(
                          rate.runCount,
                      )} de este producto en esta máquina`
                    : '';

            const finalContributionLines = rowProduct.product_id
                ? (
                      plannedProductContributions.get(
                          rowProduct.product_id,
                      ) || []
                  ).map(
                      (contribution) =>
                          `• ${contribution.machineName}: ${bothUnits(
                              contribution.kilos,
                          )}`,
                  )
                : [];

            const finalBreakdown =
                finalKilos !== null && inventoryEntry
                    ? [
                          `Final ${bothUnits(finalKilos)}:`,
                          `• Inventario ${bothUnits(inventoryEntry.kilos)}`,
                          inventoryEntry.kilos_committed > 0
                              ? `• comprometidos ${bothUnits(
                                    inventoryEntry.kilos_committed,
                                )} (solo aviso; no se resta del inventario)`
                              : '',
                          ...finalContributionLines,
                      ].filter((part) => part.length > 0)
                    : [];

            const rateFigure =
                rate && rate.value !== null
                    ? showBultos
                        ? `${formatFloat(rate.value / groupWeight, 1)} bultos/hr`
                        : `${formatFloat(rate.value, 0)} kg/hr`
                    : '';

            // The explanation the figures used to carry when they lived on the
            // product line. Kept — a bare `29 → 102*` is exactly the kind of
            // number that needs to say where it came from, and now that the
            // figures sit apart from the product names it also has to say WHICH
            // product it is about.
            const tooltip = [
                `${label}.`,
                rate && rate.status === 'ready' && rate.value !== null
                    ? `Este renglón produce ${bothUnits(lineKilos || 0)} (${rateFigure} × ${formatFloat(
                          rowProduct.hours,
                          1,
                      )} h × ${formatFloat(
                          rowProduct.efficiency,
                          0,
                      )}% de eficiencia${
                          rateSource ? `, ${rateSource}` : ''
                      }).`
                    : rate?.status === 'pending'
                    ? 'Buscando las corridas de este producto en esta máquina.'
                    : 'Esta máquina nunca ha corrido este producto: escribe las ' +
                      'horas tú, la predicción no se puede calcular.',
                ...finalBreakdown,
                inventoryEntry &&
                (inventoryEntry.kilos_committed > 0 ||
                    inventoryEntry.groups_committed > 0)
                    ? `En almacén hay ${bothUnits(
                          inventoryEntry.kilos,
                      )}, de los cuales ${bothUnits(
                          inventoryEntry.kilos_committed,
                      )} ya están vendidos y pendientes de entregar — por eso el disponible es menor.`
                    : '',
                actual
                    ? `Producción ya capturada para esta fecha, turno y máquina: ${bothUnits(
                          actual.kilos,
                      )}.`
                    : '',
            ].filter((part) => part.length > 0);

            return {
                key: rowProduct.key,
                label,
                figures,
                openText,
                tooltip,
            };
        });

        return {
            crew,
            // WHAT the row runs — names only. Hours are already represented by
            // the plan's warning badge when a row is unbalanced.
            // caption on the left, where a long compound_description has room
            // to wrap.
            productLines: productParts.map((part) => ({
                key: part.key,
                label: part.label,
            })),
            // WHAT IT PRODUCES — numbers only, no product names. Rendered on
            // the right, in the slot the hours badge used to own. Separated so
            // the figures are readable as a column down twenty rows, which they
            // are not when each one trails a 90-character product description.
            //
             // With several products the order matches the product lines on the
             // left and the product lines inside an open row.
            //
            // An ARRAY, not a joined string, so each product's figures keep
            // their own tooltip — on a two-product row a single tooltip over
            // the lot could not say which number it was explaining.
            figures: productParts.map((part) => ({
                key: part.key,
                text: part.figures,
                openText: part.openText,
                tooltip: part.tooltip,
            })),
        };
    }, [
        row.employee_ids,
        row.products,
        row.machine_id,
        employees,
        products,
        inventory,
        plannedKilosByProduct,
        plannedProductContributions,
        plannedMachinesByProduct,
        actualByMachineProduct,
        selectedMachine,
        getRate,
    ]);

    const handleMachineChange = (machineId: number | null) => {
        const nextMachine =
            allMachines.find((m) => m.id === machineId) || null;
        const nextType = nextMachine?.order_production_type_id ?? null;
        // Changing the machine to a different production type invalidates the
        // products/employees, so drop them when the type no longer matches.
        if (nextType !== typeId) {
            onChange(index, {
                machine_id: machineId,
                products: [],
                employee_ids: [],
            });
        } else {
            onChange(index, { machine_id: machineId });
        }
    };

    const handleProductChange = (
        productIndex: number,
        patch: Partial<PlanRowProductState>,
    ) => {
        onChange(index, {
            products: row.products.map((rowProduct, i) =>
                i === productIndex ? { ...rowProduct, ...patch } : rowProduct,
            ),
        });
    };

    const handleProductRemove = (productIndex: number) => {
        onChange(index, {
            products: row.products.filter((_, i) => i !== productIndex),
        });
    };

    // New lines pre-fill with whatever is left of the turno, so the common
    // "split this machine in two" case is one click plus one edit.
    const handleAddProduct = () => {
        onChange(index, {
            products: [
                ...row.products,
                {
                    key: nextRowProductKey(),
                    id: null,
                    product_id: null,
                    hours: remainingHours,
                    efficiency: DEFAULT_EFFICIENCY,
                },
            ],
        });
    };

    // The hours badge is an EXCEPTION REPORT, not a readout: it appears while
    // the row's products do not add up to its turno, or on a collapsed row whose
    // machine deliberately has its own shorter turno. A balanced row inheriting
    // the global hours stays quiet, while `6.0/6.0 h*` makes the per-row override
    // visible without reopening it.
    //
    // A row on its own turno length keeps the * so a badge reading 5.0/6.0 h next
    // to a plan of 8 reads as deliberate rather than as a second bug.
    const overridesHours = row.shift_hours !== null;
    const showHoursBadge =
        row.products.length > 0 &&
        (!hoursBalanced || (!expanded && overridesHours));
    const hoursBadgeText = `${formatFloat(assignedHours, 1)}/${formatFloat(
        rowShiftHours,
        1,
    )} h${overridesHours ? '*' : ''}`;
    const hoursBadgeTooltip = overridesHours
        ? `Este renglón trabaja ${formatFloat(
              rowShiftHours,
              1,
          )} h, no las ${formatFloat(shiftHours, 1)} h del turno`
        : '';
    const hoursBadge =
        showHoursBadge ? (
            <Tooltip title={hoursBadgeTooltip}>
                <Typography
                    variant={'caption'}
                    color={'warning.main'}
                    sx={{ whiteSpace: 'nowrap' }}
                >
                    {hoursBadgeText}
                </Typography>
            </Tooltip>
        ) : null;

    return (
        <Paper variant={'outlined'} sx={{ mb: 1 }}>
            {/* Header: always rendered, and the click target for expanding.
                Collapsed it carries the whole row at a glance; expanded it just
                names the machine, since the editor below repeats the rest. */}
            <Box
                onClick={() => onToggleExpand(row.key)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1,
                    py: 0.75,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                }}
            >
                <IconButton size={'small'} sx={{ flexShrink: 0 }}>
                    {expanded ? (
                        <ExpandLessIcon fontSize={'inherit'} />
                    ) : (
                        <ExpandMoreIcon fontSize={'inherit'} />
                    )}
                </IconButton>

                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            minWidth: 0,
                        }}
                    >
                        <Typography variant={'subtitle2'} noWrap>
                            {selectedMachine?.name || 'Sin máquina'}
                        </Typography>
                        {hoursBadge}
                    </Box>
                    {!expanded ? (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'minmax(0, 1fr) auto',
                                columnGap: 1,
                                rowGap: 0.5,
                                alignItems: 'start',
                            }}
                        >
                            {summary.crew ? (
                                <Typography
                                    variant={'caption'}
                                    color={'text.secondary'}
                                    sx={{ gridColumn: '1 / -1' }}
                                >
                                    {summary.crew}
                                </Typography>
                            ) : null}
                            {summary.productLines.length > 0 ? (
                                summary.productLines.map((line, position) => {
                                    const figure = summary.figures[position];
                                    return (
                                        <React.Fragment key={line.key}>
                                            <Typography
                                                variant={'caption'}
                                                color={'text.secondary'}
                                                sx={{ minWidth: 0 }}
                                            >
                                                {line.label}
                                            </Typography>
                                             {figure ? (
                                                 <Tooltip
                                                     title={
                                                         <PlanTooltip
                                                             paragraphs={
                                                                 figure.tooltip
                                                             }
                                                         />
                                                     }
                                                     slotProps={{
                                                         tooltip: {
                                                             sx: { maxWidth: 460 },
                                                         },
                                                     }}
                                                 >
                                                    <Typography
                                                        component={'span'}
                                                        variant={'caption'}
                                                        color={'text.secondary'}
                                                        sx={{
                                                            whiteSpace: 'nowrap',
                                                            textAlign: 'right',
                                                        }}
                                                    >
                                                        {figure.text}
                                                    </Typography>
                                                </Tooltip>
                                            ) : (
                                                <Box />
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            ) : !summary.crew ? (
                                <Typography
                                    variant={'caption'}
                                    color={'text.secondary'}
                                    sx={{ gridColumn: '1 / -1' }}
                                >
                                    Sin asignar
                                </Typography>
                            ) : null}
                        </Box>
                    ) : null}
                </Box>

                {canEdit ? (
                    // stopPropagation so the action buttons don't also toggle
                    // the accordion out from under the click.
                    <Box
                        sx={{ display: 'flex', flexShrink: 0 }}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <Tooltip title={'Subir'}>
                            <span>
                                <IconButton
                                    size={'small'}
                                    disabled={index === 0}
                                    onClick={() => onMove(index, 'up')}
                                >
                                    <KeyboardArrowUpIcon fontSize={'inherit'} />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={'Bajar'}>
                            <span>
                                <IconButton
                                    size={'small'}
                                    disabled={index === total - 1}
                                    onClick={() => onMove(index, 'down')}
                                >
                                    <KeyboardArrowDownIcon
                                        fontSize={'inherit'}
                                    />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={'Quitar'}>
                            <span>
                                <IconButton
                                    size={'small'}
                                    onClick={() => onRemove(index)}
                                >
                                    <DeleteOutlineIcon fontSize={'inherit'} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                ) : null}
            </Box>

            {/* The editor mounts ONLY while expanded — this is the whole point
                of the accordion. A 20-machine plan would otherwise hold ~60 MUI
                Autocompletes, each carrying the full product catalogue. */}
            {!expanded ? null : (
        <Box
            sx={{
                px: 1,
                pb: 1,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1,
            }}
        >
            <Box sx={{ flex: '2 1 260px', minWidth: 240 }}>
                <DIdAutocomplete
                    id={row.machine_id}
                    onChange={handleMachineChange}
                    options={machineOptions}
                    itemText={'name'}
                    label={'Máquina'}
                    disabled={!canEdit}
                    minWidth={0}
                />
            </Box>
            <Box sx={{ flex: '2 1 220px', minWidth: 200 }}>
                <DMultipleAutocomplete
                    value={selectedEmployees}
                    onChange={(vals) =>
                        onChange(index, {
                            employee_ids: vals.map((v) => v.id),
                        })
                    }
                    options={employeeOptions}
                    groupBy={groupEmployee}
                    itemText={'fullname'}
                    itemValue={'id'}
                    label={'Empleados'}
                    disabled={!canEdit || !row.machine_id}
                    disableClearable={false}
                    minWidth={0}
                    usageCounts={employeeUsage}
                />
            </Box>

            {/* This machine's own turno length. Empty = it runs the plan's
                Horas, which is the normal case; a value is for the machine that
                does not — down for maintenance, a late start. Without it the
                hard sum rule would leave such a plan unsaveable. */}
            <Tooltip
                title={
                    'Vacío = las horas del turno. Llénalo sólo si esta máquina trabaja menos.'
                }
            >
                <TextField
                    type={'number'}
                    label={'Horas máquina'}
                    size={'small'}
                    margin={'none'}
                    placeholder={formatFloat(shiftHours, 1)}
                    value={row.shift_hours ?? ''}
                    onChange={(e) => {
                        const raw = e.target.value;
                        // Clearing the field returns the row to the plan's
                        // Horas — null is a meaningful value here, not 0.
                        if (raw === '') {
                            onChange(index, { shift_hours: null });
                            return;
                        }
                        const parsed = Number(raw);
                        onChange(index, {
                            shift_hours:
                                Number.isFinite(parsed) && parsed > 0
                                    ? parsed
                                    : null,
                        });
                    }}
                    disabled={!canEdit || !row.machine_id}
                        inputProps={{ min: 0, step: HOURS_INCREMENT }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 120 }}
                />
            </Tooltip>

            {canEdit ? (
                <Tooltip title={'Agregar nota'}>
                    <span>
                        <IconButton
                            size={'small'}
                            sx={{ ml: 'auto' }}
                            onClick={() =>
                                onChange(index, {
                                    notes: [...row.notes, ''],
                                })
                            }
                        >
                            <NoteAddOutlinedIcon fontSize={'inherit'} />
                        </IconButton>
                    </span>
                </Tooltip>
            ) : null}

            {/* Planned products: a full-width block wrapping onto its own lines
                under the machine/crew row. Each line is a product + its slice of
                the turno; the footer keeps the running total against Horas. */}
            <Box sx={{ width: '100%' }}>
                {row.products.map((rowProduct, productIndex) => (
                    <Box key={rowProduct.key} sx={{ mb: 0.5 }}>
                        <PlanRowProduct
                            rowProduct={rowProduct}
                            index={productIndex}
                            machineId={row.machine_id}
                            productOptions={productOptions}
                            producedProductIds={producedProductIds}
                            allProducts={products}
                            figure={summary.figures[productIndex]}
                            canEdit={canEdit}
                            onChange={handleProductChange}
                            onRemove={handleProductRemove}
                        />
                    </Box>
                ))}

                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    {canEdit ? (
                        <Button
                            size={'small'}
                            startIcon={<AddIcon />}
                            onClick={handleAddProduct}
                            disabled={!row.machine_id}
                        >
                            Agregar producto
                        </Button>
                    ) : null}
                    {/* Only when they do NOT add up. A row that balances is the
                        normal state; saying "8.0 / 8.0" on every one of them is
                        noise that hides the row that does not. */}
                    {row.products.length > 0 && !hoursBalanced ? (
                        <Typography
                            variant={'caption'}
                            color={'warning.main'}
                        >
                            {`Horas asignadas: ${formatFloat(
                                assignedHours,
                                1,
                            )} / ${formatFloat(rowShiftHours, 1)}`}
                            {assignedHours < rowShiftHours
                                ? ` — faltan ${formatFloat(
                                      rowShiftHours - assignedHours,
                                      1,
                                  )} h`
                                : ` — sobran ${formatFloat(
                                      assignedHours - rowShiftHours,
                                      1,
                                  )} h`}
                        </Typography>
                    ) : null}
                </Box>
            </Box>

            {/* Row notes: a full-width block that wraps to its own line under
                the main flex row. The add affordance is the "Agregar nota" icon
                button in the action cluster above, so this block renders nothing
                until a note exists — a row without notes stays on one line. */}
            {row.notes.length > 0 ? (
                <Box sx={{ width: '100%' }}>
                    <NotesBulletList
                        items={row.notes}
                        onChange={(items) => onChange(index, { notes: items })}
                        canEdit={canEdit}
                        showAddButton={false}
                    />
                </Box>
            ) : null}
        </Box>
            )}
        </Paper>
    );
}
